/**
 * Central People & Access vocabulary (CT-5576). Every user type, status, access level,
 * permission-source tag and role kind is defined ONCE here as an enum plus a meta map
 * `{ labelKey, variant }`: `labelKey` is an i18n key in the `people-access` namespace and
 * `variant` a `Badge` variant from @aimsos/ui. Components render `t(meta.labelKey)` with the
 * variant; they never carry the literal or an inline color.
 *
 * System-role names are NOT part of this file: they are backend data, rendered from the API.
 */

/** Keys of one `terms.<group>` bundle, in the shape `{ [value]: { labelKey, variant } }`. */
const buildMeta = (group, variants) =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(variants).map(([value, variant]) => [
        value,
        Object.freeze({ labelKey: `terms.${group}.${value}`, variant }),
      ]),
    ),
  );

export const USER_TYPE = Object.freeze({ MEMBER: 'member', ADMIN: 'admin', OWNER: 'owner' });

export const USER_TYPE_META = buildMeta('userType', {
  [USER_TYPE.MEMBER]: 'neutral',
  [USER_TYPE.ADMIN]: 'informative',
  [USER_TYPE.OWNER]: 'danger',
});

export const MEMBER_STATUS = Object.freeze({
  ACTIVE: 'active',
  INVITED: 'invited',
  EXPIRED: 'expired',
  NOT_INVITED: 'notInvited',
  SUSPENDED: 'suspended',
  REMOVED: 'removed',
});

/** Statuses of people who have not signed in yet: no invitation sent, sent, or expired. */
export const PENDING_MEMBER_STATUSES = Object.freeze([
  MEMBER_STATUS.NOT_INVITED,
  MEMBER_STATUS.INVITED,
  MEMBER_STATUS.EXPIRED,
]);

export const MEMBER_STATUS_META = buildMeta('status', {
  [MEMBER_STATUS.ACTIVE]: 'success',
  [MEMBER_STATUS.INVITED]: 'informative',
  [MEMBER_STATUS.EXPIRED]: 'warning',
  [MEMBER_STATUS.NOT_INVITED]: 'neutral',
  [MEMBER_STATUS.SUSPENDED]: 'neutral',
  [MEMBER_STATUS.REMOVED]: 'secondary',
});

/** The transient "New" badge: a flag next to the status, not a status of its own. */
export const NEW_BADGE_META = Object.freeze({ labelKey: 'terms.status.new', variant: 'limeGreen' });

export const ACCESS_LEVEL = Object.freeze({ OWN: 'own', GROUPS: 'groups', TENANT: 'tenant' });

export const ACCESS_LEVEL_META = buildMeta('accessLevel', {
  [ACCESS_LEVEL.OWN]: 'neutral',
  [ACCESS_LEVEL.GROUPS]: 'lightBlue',
  [ACCESS_LEVEL.TENANT]: 'informative',
});

export const PERMISSION_SOURCE = Object.freeze({
  VIA_ROLE: 'viaRole',
  DIRECT: 'direct',
  PINNED: 'pinned',
  MODIFIED: 'modified',
});

/** `viaRole` takes the role name from the API: `t(labelKey, { role })`. */
export const PERMISSION_SOURCE_META = buildMeta('source', {
  [PERMISSION_SOURCE.VIA_ROLE]: 'informative',
  [PERMISSION_SOURCE.DIRECT]: 'neutral',
  [PERMISSION_SOURCE.PINNED]: 'purple',
  [PERMISSION_SOURCE.MODIFIED]: 'warning',
});

export const ROLE_KIND = Object.freeze({ SYSTEM: 'system', CUSTOM: 'custom' });

/** `labelKey` is the singular tag; `headingKey` the plural group heading (System Roles / Custom Roles). */
export const ROLE_KIND_META = Object.freeze(
  Object.fromEntries(
    Object.entries(buildMeta('roleKind', { [ROLE_KIND.SYSTEM]: 'secondary', [ROLE_KIND.CUSTOM]: 'informative' })).map(
      ([value, meta]) => [value, Object.freeze({ ...meta, headingKey: `terms.roleKindHeading.${value}` })],
    ),
  ),
);

/** Badge color per studio id, as in the prototype (Governance green, Data blue, Agentic purple). */
export const STUDIO_BADGE_VARIANT = Object.freeze({
  governance: 'limeGreen',
  data: 'lightBlue',
  agentic: 'purple',
});

/** Recurring note: invite flow step 3 and the Groups pages. One key, one wording. */
export const GROUPS_DO_NOT_GRANT_ACCESS_KEY = 'terms.groupsDoNotGrantAccess';
